import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Box, LinearProgress } from '@mui/material';
import { getReport, getCAReportInvoices, createCAReportInvoice, deleteCAReportInvoice } from '../components/report/report.api';
import keys from '../common/react-query/data-keys';
import { handleError } from '../common/utils/util.errors';
import InvoicePage from './InvoicePage';
import { toast } from 'react-toastify';
import { CreateCAReportInvoiceDTO } from '../components/report/report.type';
import { capitalize } from '../common/utils/common.util';
import { BaseInvoiceDTO, CreateInvoiceDTO } from '../components/invoice/invoice.type';
import { useAppContext } from '../common/AppContext';
import { exportInvoiceAsPDF } from '../components/invoice/invoice.util';

const CostAnalysisInvoicePage: React.FC = () => {
  const { reportId } = useParams();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const {
    data: report,
    isLoading: isLoadingReport,
    isError: isErrorInReport,
    error: errorInReport,
  } = useQuery([keys.caReport, reportId], () => getReport(reportId!), {
    enabled: Boolean(reportId),
  });

  const {
    data: invoices = [],
    isLoading: isLoadingInvoices,
    isError: isErrorInvoices,
    error: errorInvoices,
  } = useQuery([keys.caReport, reportId, 'invoices'], () => getCAReportInvoices(reportId!), {
    enabled: Boolean(reportId),
  });

  useEffect(() => {
    if (report?.generalInfo?.reportName) {
      setPageHeaderText(`Invoice / ${report.generalInfo.reportName}`);
    } else {
      setPageHeaderText('Invoice');
    }

    // Cleanup function to reset page header when component unmounts
    return () => {
      setPageHeaderText('');
    };
  }, [report?.generalInfo?.reportName, setPageHeaderText]);

  if (isLoadingReport || isLoadingInvoices) return <LinearProgress />;
  if (isErrorInReport) {
    handleError(errorInReport);
    return null;
  }
  if (isErrorInvoices) {
    handleError(errorInvoices);
    return null;
  }

  if (!report) {
    return <Box>Report not found</Box>;
  }

  const handleExportInvoice = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Check for duplicate invoice numbers
      const trimmedInvoiceNumber = invoiceData.invoice.number.trim();
      const isDuplicate = invoices.some(
        existingInvoice => existingInvoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase(),
      );

      if (isDuplicate) {
        toast.error('Invoice number already exists');
        return;
      }

      // Transform the invoice data to match the backend DTO
      const createInvoiceDto: CreateCAReportInvoiceDTO = {
        caReportId: report.id,
        invoice: {
          number: invoiceData.invoice.number,
          invoiceDate: invoiceData.invoice.invoiceDate,
          invoiceAmount: invoiceData.invoice.invoiceAmount,
          paidAmount: invoiceData.invoice.paidAmount,
        },
        recepient: invoiceData.recepient,
        recepientAddress: invoiceData.recepientAddress,
        projectName: invoiceData.projectName,
        projectAddress: {
          street: invoiceData.projectAddress?.street || '',
          city: invoiceData.projectAddress?.city || '',
          state: invoiceData.projectAddress?.state?.toUpperCase() || '',
          postalCode: invoiceData.projectAddress?.postalCode || '',
        },
        service: 'Plan and Cost Review',
      };

      // Save the invoice to the backend
      const savedInvoice = await createCAReportInvoice(createInvoiceDto);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.caReport, reportId, 'invoices']);

      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.project?.projectNumber,
          projectTitle: report.project?.projectTitle,
          clientName: report.project?.client?.name || '',
          clientAddress: {
            street: report.project?.client?.address?.street || '',
            city: capitalize(report.project?.client?.address?.city) || '',
            state: report.project?.client?.address?.state?.toUpperCase() || '',
            postalCode: report.project?.client?.address?.postalCode || '',
          },
          projectAddress: {
            street: report.project?.projectAddress?.street || '',
            city: capitalize(report.project?.projectAddress?.city) || '',
            state: report.project?.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.project?.projectAddress?.postalCode || '',
          },
        },
        report.project?.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to save invoice');
    }
  };

  // Function to check for duplicate invoice numbers
  const checkDuplicateInvoice = (invoiceNumber: string): boolean => {
    const trimmedInvoiceNumber = invoiceNumber.trim();
    return invoices.some(existingInvoice => existingInvoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase());
  };

  // Function to export and save new invoice (for CreateInvoice form)
  const handleExportAndSave = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Check for duplicate invoice numbers
      if (checkDuplicateInvoice(invoiceData.invoice.number)) {
        toast.error('Invoice number already exists');
        return;
      }

      // Transform the invoice data to match the backend DTO
      const createInvoiceDto: CreateCAReportInvoiceDTO = {
        caReportId: report.id,
        invoice: {
          number: invoiceData.invoice.number,
          invoiceDate: invoiceData.invoice.invoiceDate,
          invoiceAmount: invoiceData.invoice.invoiceAmount,
          paidAmount: invoiceData.invoice.paidAmount,
        },
        recepient: invoiceData.recepient,
        recepientAddress: invoiceData.recepientAddress,
        projectName: invoiceData.projectName,
        projectAddress: {
          street: invoiceData.projectAddress?.street || '',
          city: invoiceData.projectAddress?.city || '',
          state: invoiceData.projectAddress?.state?.toUpperCase() || '',
          postalCode: invoiceData.projectAddress?.postalCode || '',
        },
        service: 'Plan and Cost Review',
      };

      // Save the invoice to the backend
      const savedInvoice = await createCAReportInvoice(createInvoiceDto);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.caReport, reportId, 'invoices']);

      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.project?.projectNumber,
          projectTitle: report.project?.projectTitle,
          clientName: report.project?.client?.name || '',
          clientAddress: {
            street: report.project?.client?.address?.street || '',
            city: capitalize(report.project?.client?.address?.city) || '',
            state: report.project?.client?.address?.state?.toUpperCase() || '',
            postalCode: report.project?.client?.address?.postalCode || '',
          },
          projectAddress: {
            street: report.project?.projectAddress?.street || '',
            city: capitalize(report.project?.projectAddress?.city) || '',
            state: report.project?.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.project?.projectAddress?.postalCode || '',
          },
        },
        report.project?.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to save invoice');
    }
  };

  // Function to export only (for past invoices)
  const handleExport = async (invoiceData: CreateInvoiceDTO) => {
    try {
      // Export PDF using the utility function
      await exportInvoiceAsPDF(
        invoiceData,
        {
          projectNumber: report.project?.projectNumber,
          projectTitle: report.project?.projectTitle,
          clientName: report.project?.client?.name || '',
          clientAddress: {
            street: report.project?.client?.address?.street || '',
            city: capitalize(report.project?.client?.address?.city) || '',
            state: report.project?.client?.address?.state?.toUpperCase() || '',
            postalCode: report.project?.client?.address?.postalCode || '',
          },
          projectAddress: {
            street: report.project?.projectAddress?.street || '',
            city: capitalize(report.project?.projectAddress?.city) || '',
            state: report.project?.projectAddress?.state?.toUpperCase() || '',
            postalCode: report.project?.projectAddress?.postalCode || '',
          },
        },
        report.project?.projectNumber,
      );
    } catch (error) {
      handleError(error);
      toast.error('Failed to export invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    try {
      await deleteCAReportInvoice(invoiceId);

      // Invalidate the invoices query to refresh the list
      await queryClient.invalidateQueries([keys.caReport, reportId, 'invoices']);

      toast.success('Invoice deleted successfully!');
    } catch (error) {
      handleError(error);
      toast.error('Failed to delete invoice');
    }
  };

  // Transform the report data to match the expected interface
  const reportData = {
    id: report.id,
    name: report.generalInfo?.reportName,
    project: {
      projectNumber: report.project?.projectNumber,
      projectTitle: report.project?.projectTitle,
      clientName: report.project?.client?.name || '',
      clientAddress: {
        street: report.project?.client?.address?.street || '',
        city: capitalize(report.project?.client?.address?.city) || '',
        state: report.project?.client?.address?.state?.toUpperCase() || '',
        postalCode: report.project?.client?.address?.postalCode || '',
      },
      projectAddress: {
        street: report.project?.projectAddress?.street || '',
        city: capitalize(report.project?.projectAddress?.city) || '',
        state: report.project?.projectAddress?.state?.toUpperCase() || '',
        postalCode: report.project?.projectAddress?.postalCode || '',
      },
    },
  };

  // Transform invoices to match the expected interface
  const transformedInvoices: BaseInvoiceDTO[] = invoices.map(invoice => ({
    id: invoice.id,
    invoice: {
      id: invoice.invoice.id,
      number: invoice.invoice.number,
      invoiceDate: invoice.invoice.invoiceDate,
      invoiceAmount: invoice.invoice.invoiceAmount,
      paidAmount: invoice.invoice.paidAmount,
    },
    recepient: invoice.recepient,
    recepientAddress: invoice.recepientAddress,
    projectName: invoice.projectName,
    projectAddress: invoice.projectAddress,
    service: invoice.service,
    createdOn: invoice.createdOn,
    updatedOn: invoice.updatedOn,
  }));

  return (
    <InvoicePage
      reportData={reportData}
      invoices={transformedInvoices}
      onExportInvoice={handleExportAndSave}
      onExportPastInvoice={handleExport}
      onDeleteInvoice={handleDeleteInvoice}
      title={`Invoice / ${report.generalInfo?.reportName || ''}`}
      isExportMode={true}
      service='Plan and Cost Review'
    />
  );
};

export default CostAnalysisInvoicePage;
